import React, { useEffect, useMemo, useState } from 'react';
import { recoverMessageAddress } from 'viem';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import { ShieldAlert } from 'lucide-react';
import { TakeoverAgentCard } from '@/components/spec/demo/TakeoverAgentCard';

const short = (a) => `${a.slice(0, 8)}...${a.slice(-6)}`;
const ORDER = 'ORD-20001';

export function DemoTakeover({ demo }) {
  const keys = useMemo(() => ({ person: privateKeyToAccount(generatePrivateKey()), approved: privateKeyToAccount(generatePrivateKey()), impostor: privateKeyToAccount(generatePrivateKey()) }), []);
  const [results, setResults] = useState({});
  const approval = `I approve agent ${keys.approved.address} to send claims for ${ORDER} at shop.example`;
  const [approvalSig, setApprovalSig] = useState('');
  useEffect(() => { keys.person.signMessage({ message: approval }).then(setApprovalSig); }, [keys, approval]);

  const send = async (who) => {
    const agent = keys[who];
    const message = `Cancel ${ORDER} at shop.example`;
    const signer = await recoverMessageAddress({ message, signature: await agent.signMessage({ message }) });
    const ok = signer.toLowerCase() === keys.approved.address.toLowerCase();
    if (ok) await demo.submit({ principal: keys.person.address, agentId: 'agent.example/assistant', orderRef: ORDER, claimType: 'cancellation', claimNumber: 7, notice: 'Cancel before shipping', merchant: 'shop.example', expiry: 4102444800 });
    setResults((r) => ({ ...r, [who]: { ok, signer } }));
  };

  return (
    <section className="rounded-2xl border border-red-500/25 bg-red-500/[0.03] p-6 mb-6" data-testid="demo-takeover">
      <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-300" /> Takeover case. A different agent tries to act for the person</h2>
      <p className="text-sm text-gray-400 mb-4 max-w-3xl">The person signed an approval naming one agent's key. Any claim must be signed by that key. A fraudster with a different agent can copy the words but not the key.</p>
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 mb-4 text-sm" data-testid="takeover-approval">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Person's signed approval</div>
        <div className="font-mono text-xs text-gray-300 break-all">{approval}</div>
        <div className="text-xs text-gray-500 mt-1">Signed by the person's test key <span className="font-mono">{short(keys.person.address)}</span>{approvalSig && <>, signature <span className="font-mono" data-testid="takeover-approval-sig">{short(approvalSig)}</span></>}</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <TakeoverAgentCard id="approved" title="Approved agent" name="agent.example/assistant" address={keys.approved.address} result={results.approved} onSend={() => send('approved')} />
        <TakeoverAgentCard id="impostor" title="Different agent" name="agent.example/assistant (copied name)" address={keys.impostor.address} result={results.impostor} onSend={() => send('impostor')} />
      </div>
    </section>
  );
}
